// src/components/headerone/bots/Omni8x8Bot.js
// Omni 8x8 bot component - receives userData from Cognito and initializes Omni 8x8 bot
import React, { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { extractUserDataFromPayload } from '../SharedHeader';

const OMNI_ORIGIN = 'https://app.demo.omnibots.ai';
const IFRAME_ID = 'iframe-id'; // Omni bot script uses standard 'iframe-id' for both bots

const wait = (ms) => new Promise(r => setTimeout(r, ms));

function getTargetOrigin(el) {
  try {
    const base = typeof document !== 'undefined' ? document.baseURI : undefined;
    return el?.src ? new URL(el.src, base).origin : OMNI_ORIGIN;
  } catch { return OMNI_ORIGIN; }
}

async function waitForIframe(intervalMs = 100, signal) {
  for (;;) {
    if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
    const el = document.getElementById(IFRAME_ID);
    if (el && el.contentWindow && isWidgetOpen()) return el;
    await wait(intervalMs);
  }
}

async function sendOmni(event, payload, { waitForEl = true, signal, immediate = false } = {}) {
  let el = document.getElementById(IFRAME_ID);

  // If immediate flag is set and iframe is ready, send immediately without waiting
  if (immediate && el && el.contentWindow && isWidgetOpen()) {
    const origin = getTargetOrigin(el);
    const msg = { channel: 'omnibot', event, payload, ts: Date.now() };
    console.info('[OMNI8x8] Sending message immediately', { 
      event, 
      origin, 
      pageId: payload?.pageId,
      fullPayload: payload,
      message: msg,
      iframeSrc: el.src
    });
    el.contentWindow.postMessage(msg, origin);
    console.info('[OMNI8x8] sent immediately', { event, origin, pageId: payload?.pageId });
    return 'sent';
  }

  if (waitForEl && (!el || !el.contentWindow || !isWidgetOpen())) {
    // Only log if we're actually waiting (not just checking)
    // Reduce verbosity - only log once per attempt
    const logKey = `omni8x8-wait-${event}`;
    if (!window[logKey]) {
      window[logKey] = true;
      console.info('[OMNI8x8] waiting for OPEN iframe…');
      setTimeout(() => { window[logKey] = false; }, 5000); // Reset after 5s
    }
    el = await waitForIframe(100, signal);
  }

  if (!el || !el.contentWindow || !isWidgetOpen()) {
    // Don't warn for context events when widget is closed - this is expected
    if (event !== 'context') {
      console.warn('[OMNI8x8] iframe not open/available');
    }
    return 'missing';
  }

  await new Promise(r => requestAnimationFrame(() => requestAnimationFrame(r)));

  const origin = getTargetOrigin(el);
  const msg = { channel: 'omnibot', event, payload, ts: Date.now() };
  el.contentWindow.postMessage(msg, origin);
  console.info('[OMNI8x8] sent', { event, origin });
  return 'sent';
}

function isWidgetOpen() {
  const btn = document.getElementById('mainButton');
  if (!btn) return false;
  const expanded = btn.getAttribute('aria-expanded');
  if (expanded === 'true') return true;
  if (expanded === 'false') return false;
  return !btn.classList.contains('df-closed');
}

const getBrowserDetails = () => {
  const ua = navigator.userAgent || '';
  const mCriOS = ua.match(/CriOS\/([\d.]+)/);        if (mCriOS) return `Chrome iOS ${mCriOS[1]}`;
  const mFxiOS = ua.match(/FxiOS\/([\d.]+)/);        if (mFxiOS) return `Firefox iOS ${mFxiOS[1]}`;
  const mEdgiOS = ua.match(/EdgiOS\/([\d.]+)/);      if (mEdgiOS) return `Edge iOS ${mEdgiOS[1]}`;
  const mOPiOS = ua.match(/OPiOS\/([\d.]+)/);        if (mOPiOS) return `Opera iOS ${mOPiOS[1]}`;
  const mEdge  = ua.match(/Edg\/([\d.]+)/);          if (mEdge)  return `Edge ${mEdge[1]}`;
  const mOPR   = ua.match(/OPR\/([\d.]+)/);          if (mOPR)   return `Opera ${mOPR[1]}`;
  const mFirefox = ua.match(/Firefox\/([\d.]+)/);    if (mFirefox) return `Firefox ${mFirefox[1]}`;
  const mChrome  = /Chrome\/([\d.]+)/.exec(ua);      if (mChrome && !/Chromium|OPR|Edg/.test(ua)) return `Chrome ${mChrome[1]}`;
  const mSafari  = /Safari\/[\d.]+/.test(ua) && ua.match(/Version\/([\d.]+)/);
  if (mSafari) return `Safari ${mSafari[1]}`;
  const mChromium = ua.match(/Chromium\/([\d.]+)/);  if (mChromium) return `Chromium ${mChromium[1]}`;
  const brand = navigator.userAgentData?.brands?.find(b => b.brand && b.brand !== 'Chromium')
    || navigator.userAgentData?.brands?.[0];
  if (brand) return `${brand.brand} ${brand.version}`;
  return 'Unknown';
};

const getOS = () => {
  const ua = navigator.userAgent || '';
  if (/Windows NT/i.test(ua)) return 'Windows';
  if (/Macintosh|Mac OS X/i.test(ua)) return 'macOS';
  if (/iPhone|iPad|iPod/i.test(ua)) return 'iOS';
  if (/Android/i.test(ua)) return 'Android';
  if (/CrOS/i.test(ua)) return 'Chrome OS';
  if (/Linux/i.test(ua)) return 'Linux';
  return navigator.platform || 'Unknown';
};

const getScreenResolution = () => `${window.screen?.width || 0}x${window.screen?.height || 0}`;

// Global flag to track if script is loaded (shared across all Omni bot instances)
// Use window-level to share across modules (same as OmniBot)
if (typeof window !== 'undefined' && !window.__omnibotScriptLoaded) {
  window.__omnibotScriptLoaded = false;
}
// Track last time we re-pushed config to prevent spam
let lastConfigPushTime8x8 = 0;
const CONFIG_PUSH_COOLDOWN = 2000; // Don't re-push more than once every 2 seconds

// Global bot visibility monitor is shared with OmniBot.js (only initialize once)
// The monitor runs in OmniBot.js and handles both bots

// Global interceptor to prevent bot elements from being removed
// This runs once globally and persists across component remounts
if (typeof window !== 'undefined' && !window.__omni8x8ElementProtection) {
  window.__omni8x8ElementProtection = true;
  
  // Intercept removeChild and remove methods to prevent bot element removal
  const originalRemoveChild = Node.prototype.removeChild;
  const originalRemove = Element.prototype.remove;
  
  Node.prototype.removeChild = function(child) {
    if (child && typeof child === 'object') {
      // Check if this is a bot element (both Omni bots use the same IDs)
      const isBotElement = child.id === 'mainButton' || 
                           child.id === 'iframe-id' ||
                           child.id === 'question-box' ||
                           (child.querySelector && (
                             child.querySelector('#mainButton') ||
                             child.querySelector('#iframe-id')
                           ));
      
      // Only protect if the active bot is omni8x8
      if (isBotElement && document.body?.dataset?.gtsActiveBot === 'omni8x8') {
        console.warn('[OMNI8x8 Protection] Prevented removal of bot element via removeChild', {
          id: child.id,
          tagName: child.tagName,
          parent: this.tagName
        });
        // Don't actually remove - just return the child
        return child;
      }
    }
    return originalRemoveChild.call(this, child);
  };
  
  Element.prototype.remove = function() {
    // Check if this is a bot element (both Omni bots use the same IDs)
    const isBotElement = this.id === 'mainButton' || 
                         this.id === 'iframe-id' ||
                         this.id === 'question-box' ||
                         (this.querySelector && (
                           this.querySelector('#mainButton') ||
                           this.querySelector('#iframe-id')
                         ));
    
    // Only protect if the active bot is omni8x8
    if (isBotElement && document.body?.dataset?.gtsActiveBot === 'omni8x8') {
      console.warn('[OMNI8x8 Protection] Prevented removal of bot element via remove()', {
        id: this.id,
        tagName: this.tagName
      });
      // Don't actually remove - just ensure it stays visible
      this.style.setProperty('display', 'block', 'important');
      this.style.setProperty('visibility', 'visible', 'important');
      this.style.setProperty('opacity', '1', 'important');
      return;
    }
    return originalRemove.call(this);
  };
  
  console.log('[OMNI8x8 Protection] Global element protection installed');
  
  // Also set up a continuous monitor to restore elements if they disappear
  // This runs independently of React and checks every 100ms
  const visibilityMonitor = setInterval(() => {
    if (document.body?.dataset?.gtsActiveBot === 'omni8x8') {
      const btn = document.getElementById('mainButton');
      const iframe = document.getElementById('iframe-id');
      
      if (btn || iframe) {
        // Elements exist - ensure they're visible
        [btn, iframe].forEach((el) => {
          if (el) {
            const computed = window.getComputedStyle(el);
            if (computed.display === 'none' || computed.visibility === 'hidden' || computed.opacity === '0') {
              console.warn('[OMNI8x8 Protection] Element was hidden, restoring visibility', {
                id: el.id,
                display: computed.display,
                visibility: computed.visibility,
                opacity: computed.opacity
              });
              el.style.setProperty('display', 'block', 'important');
              el.style.setProperty('visibility', 'visible', 'important');
              el.style.setProperty('opacity', '1', 'important');
            }
          }
        });
      } else {
        // Elements don't exist - they might have been removed
        // Try to restore by re-pushing config if script is loaded
        const scriptLoaded = document.getElementById('omnibot-launcher') !== null;
        if (scriptLoaded && window._omnibotJs && Array.isArray(window._omnibotJs)) {
          // Check if we recently pushed config (avoid spam)
          const lastPush = window.__omni8x8LastConfigPush || 0;
          const now = Date.now();
          if ((now - lastPush) > 2000) { // 2 second cooldown
            window.__omni8x8LastConfigPush = now;
            // Re-push the last known config
            const lastConfig = window.__omni8x8LastConfig;
            if (lastConfig) {
              console.warn('[OMNI8x8 Protection] Elements missing, re-pushing config to restore', lastConfig);
              window._omnibotJs.push(lastConfig);
            }
          }
        }
      }
    }
  }, 100); // Check every 100ms
  
  // Store the interval so we can clear it if needed (though we probably won't)
  window.__omni8x8VisibilityMonitor = visibilityMonitor;
}

// Component definition - will be wrapped with React.memo at export
function Omni8x8Bot({ userData, payload }) {
  const location = useLocation();
  const firstRunRef = useRef(true);
  const mountTimeRef = useRef(Date.now());
  
  // Log component lifecycle
  useEffect(() => {
    console.log('[OMNI8x8] Component mounted', { mountTime: mountTimeRef.current });
    return () => {
      console.log('[OMNI8x8] Component unmounting', {
        pathname: location.pathname,
        btnExists: !!document.getElementById('mainButton'),
        iframeExists: !!document.getElementById(IFRAME_ID),
        bodyAttr: document.body?.dataset?.gtsActiveBot
      });
      // CRITICAL: When component unmounts, ensure bot elements persist in DOM
      // Don't let React cleanup remove them - they should persist across route changes
      const btn = document.getElementById('mainButton');
      const iframe = document.getElementById(IFRAME_ID);
      if (btn || iframe) {
        console.warn('[OMNI8x8] Component unmounting but elements exist - ensuring they persist', {
          btn: !!btn,
          iframe: !!iframe
        });
        // Force elements to stay in DOM by moving them to body if needed
        // This prevents them from being removed when component unmounts
        [btn, iframe].forEach((el) => {
          if (el && el.parentNode && el.parentNode !== document.body) {
            // If element is not directly in body, ensure it stays visible
            el.style.setProperty('display', 'block', 'important');
            el.style.setProperty('visibility', 'visible', 'important');
            el.style.setProperty('opacity', '1', 'important');
          }
        });
      }
    };
  }, []);

  // Map Cognito userData to Omni format
  // Omni expects: customerID (last 4), customerName, emailAddress
  const customerID = userData?.customerID || '';
  const customerName = userData?.fullName || '';
  const emailAddress = userData?.emailAddress || '';

  // Logging for debugging
  useEffect(() => {
    console.log('[OMNI8x8] Component mounted/updated', {
      pathname: location.pathname,
      search: location.search,
      bodyAttr: document.body?.dataset?.gtsActiveBot,
      btnExists: !!document.getElementById('mainButton'),
      iframeExists: !!document.getElementById(IFRAME_ID),
      mountTime: mountTimeRef.current,
      currentTime: Date.now()
    });
  }, [location.pathname, location.search]);

  // CRITICAL: Ensure body attribute is set when this component is mounted/active
  // This must run before the bootstrap component to ensure CSS visibility rules work
  useEffect(() => {
    console.log('[OMNI8x8] Mount effect running');
    if (typeof document !== 'undefined' && document.body) {
      const prevAttr = document.body.dataset.gtsActiveBot;
      document.body.dataset.gtsActiveBot = 'omni8x8';
      if (prevAttr !== 'omni8x8') {
        console.log('[OMNI8x8] Body attribute changed', { from: prevAttr, to: 'omni8x8' });
      }
      
      // Set up a persistent interval to ensure bot stays visible
      // This runs even if component unmounts/remounts
      const visibilityCheck = setInterval(() => {
        const currentAttr = document.body.dataset.gtsActiveBot;
        if (currentAttr === 'omni8x8') {
          const btn = document.getElementById('mainButton');
          const iframe = document.getElementById(IFRAME_ID);
          if (btn || iframe) {
            // Bot elements exist - ensure they're visible
            [btn, iframe].forEach((el) => {
              if (el) {
                const computed = window.getComputedStyle(el);
                if (computed.display === 'none' || computed.visibility === 'hidden' || computed.opacity === '0') {
                  console.warn('[OMNI8x8] Element hidden in visibility check', {
                    id: el.id,
                    display: computed.display,
                    visibility: computed.visibility,
                    opacity: computed.opacity
                  });
                  el.style.setProperty('display', 'block', 'important');
                  el.style.setProperty('visibility', 'visible', 'important');
                  el.style.setProperty('opacity', '1', 'important');
                }
              }
            });
          } else {
            // Log when elements are missing
            console.warn('[OMNI8x8] Elements missing in visibility check', {
              bodyAttr: currentAttr,
              btnExists: !!btn,
              iframeExists: !!iframe
            });
          }
        } else {
          console.warn('[OMNI8x8] Body attribute wrong in visibility check', {
            expected: 'omni8x8',
            actual: currentAttr
          });
        }
      }, 500); // Check every 500ms
      
      return () => {
        console.log('[OMNI8x8] Mount effect cleanup');
        clearInterval(visibilityCheck);
      };
    }
  }, []); // Only run once on mount

  // Also ensure it's set on route changes (defensive)
  useEffect(() => {
    if (typeof document !== 'undefined' && document.body) {
      // Set body attribute IMMEDIATELY and synchronously - CRITICAL
      document.body.dataset.gtsActiveBot = 'omni8x8';
      
      // Lock the body attribute to prevent it from being changed
      Object.defineProperty(document.body.dataset, 'gtsActiveBot', {
        value: 'omni8x8',
        writable: true,
        configurable: true
      });
      
      // Immediately ensure bot elements stay visible during route changes
      // This prevents the bot from disappearing during navigation
      const ensureVisibleImmediate = () => {
        // Re-set body attribute in case it was cleared
        if (document.body.dataset.gtsActiveBot !== 'omni8x8') {
          document.body.dataset.gtsActiveBot = 'omni8x8';
        }
        
        const btn = document.getElementById('mainButton');
        const iframe = document.getElementById(IFRAME_ID);
        const questionBox = document.getElementById('question-box');
        const messageBox = document.querySelector('.df-message-box');
        
        // Remove any hiding styles immediately and force visibility
        [btn, iframe, questionBox, messageBox].forEach((el) => {
          if (el) {
            el.style.removeProperty('display');
            el.style.removeProperty('opacity');
            el.style.removeProperty('visibility');
            // Always force show with !important
            el.style.setProperty('display', 'block', 'important');
            el.style.setProperty('visibility', 'visible', 'important');
            el.style.setProperty('opacity', '1', 'important');
          }
        });
      };
      
      // Run immediately and multiple times to catch any timing issues
      ensureVisibleImmediate();
      setTimeout(ensureVisibleImmediate, 0);
      setTimeout(ensureVisibleImmediate, 10);
      setTimeout(ensureVisibleImmediate, 50);
      setTimeout(ensureVisibleImmediate, 100);
      setTimeout(ensureVisibleImmediate, 200);
      setTimeout(ensureVisibleImmediate, 500);
      
      // Also run after a microtask to catch any async hiding
      Promise.resolve().then(ensureVisibleImmediate);
      
      // And run after next frame
      requestAnimationFrame(() => {
        requestAnimationFrame(ensureVisibleImmediate);
      });
    }
  }, [location.pathname, location.search]);

  function OmnibotBootstrap({ pageId }) {
    const collect = () => ({
      subjectLine: document.title || '',
      currentURL: window.location.href,
      browserDetails: getBrowserDetails(),
      screenResolution: getScreenResolution(),
      platform: getOS(),
      customerID,
      customerName,
      emailAddress,
    });

    const waitForTitleStable = (stableMs = 350, timeoutMs = 3000) =>
      new Promise(resolve => {
        const startTitle = document.title || '';
        let lastChange = Date.now();
        let resolved = false;
        let raf1 = 0, raf2 = 0;
        let timeout = 0;
        let debounce = 0;
        let obs = null;

        const done = () => {
          if (resolved) return;
          resolved = true;
          if (obs) obs.disconnect();
          if (timeout) clearTimeout(timeout);
          if (debounce) clearTimeout(debounce);
          if (raf1) cancelAnimationFrame(raf1);
          if (raf2) cancelAnimationFrame(raf2);
          raf1 = requestAnimationFrame(() => {
            raf2 = requestAnimationFrame(resolve);
          });
        };

        const armDebounce = () => {
          if (debounce) clearTimeout(debounce);
          debounce = setTimeout(() => {
            if (Date.now() - lastChange >= stableMs) done();
          }, stableMs);
        };

        const titleEl = document.querySelector('head > title');
        if (titleEl && 'MutationObserver' in window) {
          obs = new MutationObserver(() => {
            lastChange = Date.now();
            armDebounce();
          });
          obs.observe(titleEl, { childList: true, characterData: true, subtree: true });
        }

        armDebounce();
        if (document.readyState !== 'complete') {
          window.addEventListener('load', armDebounce, { once: true });
        }

        timeout = setTimeout(done, timeoutMs);

        if ((document.title || '') !== startTitle && (document.title || '') !== '') {
          lastChange = Date.now();
          armDebounce();
        }
      });

    useEffect(() => {
      console.log('[OMNI8x8] Bootstrap useEffect running', { pageId });
      const w = window;
      w._omnibotJs = w._omnibotJs || [];

      let cancelled = false;
      
      const pushConfig = (reason = 'unknown') => {
        const config = {
          deploymentId: '7d6cd007-48c0-4fe9-931c-f15a05ba0ad2',
          orgId: '9c6603a7-d5de-4f80-b5b0-6e13bfea2fe5',
          appId: '2ef241502396086a34add2327467b10b2',
          closeText: 'Virtual Assistant',
          infoText:
            "Hi, I'm the State of GTS Virtual Agent. I can answer questions, transfer to an agent and so much more.",
          language: 'en',
          userType: 'df_generic_rag_agent',
          pageId: pageId || '',
          environment: 'production',
          dataConfig: collect(),
        };
        console.log('[OMNI8x8] Pushing config', { reason, pageId: config.pageId, configCount: w._omnibotJs.length });
        w._omnibotJs.push(config);
        // Store the config globally so the protection monitor can restore it if needed
        window.__omni8x8LastConfig = config;
        window.__omni8x8LastConfigPush = Date.now();
      };

      const init = async () => {
        console.log('[OMNI8x8] Init starting', { pageId });
        
        // CRITICAL: Check if BOTH elements exist before skipping config push
        // If BOTH elements exist and are visible, the bot is already initialized
        // If elements are missing, we need to push config to recreate them
        const existingBtn = document.getElementById('mainButton');
        const existingIframe = document.getElementById(IFRAME_ID);
        
        // Check if elements exist AND are visible (not hidden by CSS)
        const isElementVisible = (el) => {
          if (!el) return false;
          const style = window.getComputedStyle(el);
          return style.display !== 'none' && style.visibility !== 'hidden' && style.opacity !== '0';
        };
        
        const btnVisible = existingBtn && isElementVisible(existingBtn);
        const iframeVisible = existingIframe && isElementVisible(existingIframe);
        
        // Only skip if BOTH elements exist AND are visible
        if (btnVisible && iframeVisible) {
          console.log('[OMNI8x8] Both elements exist and visible, skipping config push to prevent reinitialization', {
            btn: !!existingBtn,
            iframe: !!existingIframe,
            btnVisible,
            iframeVisible
          });
          // Elements exist and are visible - just ensure they stay visible
          [existingBtn, existingIframe].forEach((el) => {
            if (el) {
              el.style.setProperty('display', 'block', 'important');
              el.style.setProperty('visibility', 'visible', 'important');
              el.style.setProperty('opacity', '1', 'important');
            }
          });
          return; // Early return - don't push config or load script
        }
        
        // Elements are missing or hidden - log and continue to push config
        if (!existingBtn || !existingIframe) {
          console.warn('[OMNI8x8] Elements missing, will push config to recreate them', {
            btn: !!existingBtn,
            iframe: !!existingIframe,
            btnVisible,
            iframeVisible
          });
        } else {
          console.warn('[OMNI8x8] Elements exist but are hidden, will push config to restore them', {
            btn: !!existingBtn,
            iframe: !!existingIframe,
            btnVisible,
            iframeVisible
          });
        }
        
        await waitForTitleStable();
        if (cancelled) {
          console.log('[OMNI8x8] Init cancelled');
          return;
        }
        pushConfig('initial');

        // Only load script once globally (shared across all Omni bot instances)
        // Use the same script ID as OmniBot to ensure it's only loaded once
        if (!window.__omnibotScriptLoaded && !document.getElementById('omnibot-launcher')) {
          window.__omnibotScriptLoaded = true;
          const s = document.createElement('script');
          s.id = 'omnibot-launcher';
          s.type = 'text/javascript';
          s.async = true;
          s.src = 'https://app.demo.omnibots.ai/omnibot-launcher.js';
          const firstScript = document.getElementsByTagName('script')[0];
          firstScript.parentNode.insertBefore(s, firstScript);
        }
      };

      init();
      
      // Listen for route changes - but DON'T re-push config if elements exist
      // Re-pushing config causes the bot script to remove and recreate elements
      const handleRouteChange = (e) => {
        console.log('[OMNI8x8] Route change event received', e.detail);
        if (e.detail?.bot === 'omni8x8') {
          // Check if elements exist
          const btn = document.getElementById('mainButton');
          const iframe = document.getElementById(IFRAME_ID);
          console.log('[OMNI8x8] Route change handler check', {
            btn: !!btn,
            iframe: !!iframe,
            bodyAttr: document.body?.dataset?.gtsActiveBot
          });
          
          if (!btn && !iframe) {
            console.warn('[OMNI8x8] Elements missing in route change handler, waiting...');
            // Elements are missing - wait a bit to see if they're being recreated
            // Only re-push if they're still missing after a delay
            setTimeout(() => {
              const btn2 = document.getElementById('mainButton');
              const iframe2 = document.getElementById(IFRAME_ID);
              console.log('[OMNI8x8] After delay check', { btn: !!btn2, iframe: !!iframe2 });
              if (!btn2 && !iframe2) {
                // Elements are still missing after delay - re-push config
                const now = Date.now();
                if ((now - lastConfigPushTime8x8) > CONFIG_PUSH_COOLDOWN) {
                  lastConfigPushTime8x8 = now;
                  pushConfig('route-change-missing');
                  console.log('[OMNI8x8] Route changed, elements missing, re-pushed config');
                } else {
                  console.log('[OMNI8x8] Cooldown active, skipping re-push');
                }
              } else {
                // Elements appeared - just ensure visibility
                console.log('[OMNI8x8] Elements appeared after delay, ensuring visibility');
                [btn2, iframe2].forEach((el) => {
                  if (el) {
                    el.style.setProperty('display', 'block', 'important');
                    el.style.setProperty('visibility', 'visible', 'important');
                    el.style.setProperty('opacity', '1', 'important');
                  }
                });
              }
            }, 500); // Wait 500ms before re-pushing
          } else {
            // Elements exist - just ensure visibility, DON'T re-push config
            console.log('[OMNI8x8] Elements exist, ensuring visibility only');
            [btn, iframe].forEach((el) => {
              if (el) {
                el.style.setProperty('display', 'block', 'important');
                el.style.setProperty('visibility', 'visible', 'important');
                el.style.setProperty('opacity', '1', 'important');
              }
            });
          }
        }
      };
      
      window.addEventListener('omni:routeChange', handleRouteChange);

      return () => {
        cancelled = true;
        window.removeEventListener('omni:routeChange', handleRouteChange);
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // Only initialize script once

    // Ensure bot elements remain visible on route changes
    // This runs whenever the route changes to ensure the bot doesn't disappear
    useEffect(() => {
      console.log('[OMNI8x8] Route change visibility effect running', {
        pageId,
        pathname: location.pathname,
        search: location.search,
        bodyAttr: document.body?.dataset?.gtsActiveBot,
        btnExists: !!document.getElementById('mainButton'),
        iframeExists: !!document.getElementById(IFRAME_ID)
      });
      
      // CRITICAL: Ensure body attribute is set to "omni8x8" for CSS visibility rules IMMEDIATELY
      // Set this synchronously before anything else to prevent flicker
      if (typeof document !== 'undefined' && document.body) {
        const prevAttr = document.body.dataset.gtsActiveBot;
        document.body.dataset.gtsActiveBot = 'omni8x8';
        if (prevAttr !== 'omni8x8') {
          console.log('[OMNI8x8] Body attribute changed in visibility effect', { from: prevAttr, to: 'omni8x8' });
        }
      }
      
      // Also ensure bot elements are visible immediately (don't wait for timeouts)
      const immediateBtn = document.getElementById('mainButton');
      const immediateIframe = document.getElementById(IFRAME_ID);
      const immediateQuestionBox = document.getElementById('question-box');
      const immediateMessageBox = document.querySelector('.df-message-box');
      
      // Immediately show any existing elements
      [immediateBtn, immediateIframe, immediateQuestionBox, immediateMessageBox].forEach((el) => {
        if (el) {
          el.style.removeProperty('display');
          el.style.removeProperty('opacity');
          el.style.removeProperty('visibility');
        }
      });

      // Watch for bot elements being removed from DOM and prevent hiding
      let elementObserver = null;
      let removalObserver = null;
      const btn = document.getElementById('mainButton');
      
      // Intercept removals to prevent bot elements from being removed
      if ('MutationObserver' in window) {
        removalObserver = new MutationObserver((mutations) => {
          mutations.forEach((mutation) => {
            mutation.removedNodes.forEach((node) => {
              // Check if removed node is a bot element or contains bot elements
              if (node.nodeType === 1) { // Element node
                const isBotElement = node.id === 'mainButton' || 
                                     node.id === 'iframe-id' || 
                                     node.id === 'iframe-id-8x8' ||
                                     node.id === 'question-box' ||
                                     node.querySelector?.('#mainButton') ||
                                     node.querySelector?.('#iframe-id') ||
                                     node.querySelector?.('#iframe-id-8x8');
                
                if (isBotElement) {
                  console.error('[OMNI8x8] Bot element REMOVED from DOM!', {
                    id: node.id,
                    tagName: node.tagName,
                    className: node.className,
                    parentNode: node.parentNode?.tagName,
                    parentId: node.parentNode?.id,
                    bodyAttr: document.body?.dataset?.gtsActiveBot,
                    stackTrace: new Error().stack?.split('\n').slice(0, 10).join('\n')
                  });
                  
                  // CRITICAL: Try to restore immediately by re-inserting into DOM
                  // This might prevent the bot script from fully removing it
                  try {
                    if (node.parentNode) {
                      console.log('[OMNI8x8] Attempting to prevent removal by re-inserting');
                      // Re-insert the node immediately
                      node.parentNode.appendChild(node);
                    }
                  } catch (e) {
                    console.error('[OMNI8x8] Failed to re-insert element', e);
                  }
                  
                  // Also try to restore via config push
                  setTimeout(() => {
                    const btn = document.getElementById('mainButton');
                    const iframe = document.getElementById(IFRAME_ID);
                    if (!btn && !iframe) {
                      const scriptLoaded = document.getElementById('omnibot-launcher') !== null;
                      if (scriptLoaded && window._omnibotJs && Array.isArray(window._omnibotJs)) {
                        const now = Date.now();
                        if ((now - lastConfigPushTime8x8) > CONFIG_PUSH_COOLDOWN) {
                          lastConfigPushTime8x8 = now;
                          const config = {
                            deploymentId: '7d6cd007-48c0-4fe9-931c-f15a05ba0ad2',
                            orgId: '9c6603a7-d5de-4f80-b5b0-6e13bfea2fe5',
                            appId: '2ef241502396086a34add2327467b10b2',
                            closeText: 'Virtual Assistant',
                            infoText: "Hi, I'm the State of GTS Virtual Agent. I can answer questions, transfer to an agent and so much more.",
                            language: 'en',
                            userType: 'df_generic_rag_agent',
                            pageId: pageId || '',
                            environment: 'production',
                            dataConfig: collect(),
                          };
                          window._omnibotJs.push(config);
                          console.log('[OMNI8x8] Re-pushed config after element removal');
                        }
                      }
                    }
                  }, 100);
                }
              }
            });
          });
        });
        
        // Watch for attribute changes that might hide elements
        if (btn) {
          elementObserver = new MutationObserver((mutations) => {
            // Check if bot elements were removed
            const currentBtn = document.getElementById('mainButton');
            const currentIframe = document.getElementById(IFRAME_ID);
            if (!currentBtn && !currentIframe) {
              console.error('[OMNI8x8] ElementObserver detected elements missing!', {
                mutations: mutations.length,
                bodyAttr: document.body?.dataset?.gtsActiveBot
              });
              // Elements were removed - re-push config if script is loaded
              const scriptLoaded = document.getElementById('omnibot-launcher') !== null;
              const now = Date.now();
              if (scriptLoaded && window._omnibotJs && Array.isArray(window._omnibotJs) && (now - lastConfigPushTime8x8) > CONFIG_PUSH_COOLDOWN) {
                lastConfigPushTime8x8 = now;
                const config = {
                  deploymentId: '7d6cd007-48c0-4fe9-931c-f15a05ba0ad2',
                  orgId: '9c6603a7-d5de-4f80-b5b0-6e13bfea2fe5',
                  appId: '2ef241502396086a34add2327467b10b2',
                  closeText: 'Virtual Assistant',
                  infoText: "Hi, I'm the State of GTS Virtual Agent. I can answer questions, transfer to an agent and so much more.",
                  language: 'en',
                  userType: 'df_generic_rag_agent',
                  pageId: pageId || '',
                  environment: 'production',
                  dataConfig: collect(),
                };
                window._omnibotJs.push(config);
                console.log('[OMNI8x8] Elements removed, re-pushed config');
              } else {
                console.warn('[OMNI8x8] Cannot re-push config', {
                  scriptLoaded,
                  hasArray: !!window._omnibotJs,
                  isArray: Array.isArray(window._omnibotJs),
                  cooldown: (now - lastConfigPushTime8x8) <= CONFIG_PUSH_COOLDOWN
                });
              }
            } else {
              // Elements exist - ensure they're visible
              [currentBtn, currentIframe].forEach((el) => {
                if (el) {
                  const computed = window.getComputedStyle(el);
                  if (computed.display === 'none' || computed.visibility === 'hidden') {
                    console.warn('[OMNI8x8] ElementObserver found hidden element', {
                      id: el.id,
                      display: computed.display,
                      visibility: computed.visibility
                    });
                    el.style.setProperty('display', 'block', 'important');
                    el.style.setProperty('visibility', 'visible', 'important');
                    el.style.setProperty('opacity', '1', 'important');
                  }
                }
              });
            }
          });
          elementObserver.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['style', 'class'] });
        }
        
        // Watch document body for removals
        removalObserver.observe(document.body, { childList: true, subtree: true });
      }

      // Ensure the bot UI elements are visible when this component is active
      const ensureVisible = () => {
        // First, ensure body attribute is set
        if (typeof document !== 'undefined' && document.body) {
          document.body.dataset.gtsActiveBot = 'omni8x8';
        }

        const btn = document.getElementById('mainButton');
        const iframe = document.getElementById(IFRAME_ID);
        const questionBox = document.getElementById('question-box');
        const messageBox = document.querySelector('.df-message-box');
        
        // Check if elements exist
        const elements = [btn, iframe, questionBox, messageBox].filter(Boolean);
        
        if (elements.length === 0) {
          // Elements don't exist yet - the script might still be loading
          // OR they were removed. Try to re-push config if script is loaded
          const scriptLoaded = document.getElementById('omnibot-launcher') !== null;
          const now = Date.now();
          const shouldRepush = scriptLoaded && 
                               window._omnibotJs && 
                               Array.isArray(window._omnibotJs) &&
                               (now - lastConfigPushTime8x8) > CONFIG_PUSH_COOLDOWN;
          
          if (shouldRepush) {
            // Script is loaded but elements are missing - re-push config (with cooldown)
            lastConfigPushTime8x8 = now;
            const config = {
              deploymentId: '7d6cd007-48c0-4fe9-931c-f15a05ba0ad2',
              orgId: '9c6603a7-d5de-4f80-b5b0-6e13bfea2fe5',
              appId: '2ef241502396086a34add2327467b10b2',
              closeText: 'Virtual Assistant',
              infoText: "Hi, I'm the State of GTS Virtual Agent. I can answer questions, transfer to an agent and so much more.",
              language: 'en',
              userType: 'df_generic_rag_agent',
              pageId: pageId || '',
              environment: 'production',
              dataConfig: collect(),
            };
            window._omnibotJs.push(config);
            console.log('[OMNI8x8] Re-pushed config to _omnibotJs array (cooldown active)');
          }
          // Don't return early - continue to check visibility even if elements don't exist yet
        }

          // Only process elements if they exist
        if (elements.length > 0) {
          // Remove any inline styles that might hide the element
          // Note: CSS !important rules will still apply, but we need body attribute set
          elements.forEach((el) => {
            if (el) {
              el.style.removeProperty('display');
              el.style.removeProperty('opacity');
              el.style.removeProperty('visibility');
            }
          });

          // Force show using !important override if needed (last resort)
          // Only do this if elements exist but are still hidden
          elements.forEach((el) => {
            const computed = window.getComputedStyle(el);
            if (computed.display === 'none' || computed.visibility === 'hidden' || computed.opacity === '0') {
              // Double-check body attribute
              if (document.body.dataset.gtsActiveBot !== 'omni8x8') {
                document.body.dataset.gtsActiveBot = 'omni8x8';
                console.log('[OMNI8x8] Body attribute was wrong, fixed to "omni8x8"');
              } else {
                // Body attribute is correct but element is still hidden
                // This might be a timing issue - CSS should handle this, but log once per element
                const logKey = `omni8x8-hidden-${el.id || el.className}`;
                if (!window[logKey]) {
                  window[logKey] = true;
                  console.warn('[OMNI8x8] Element exists but is hidden despite correct body attribute:', el.id || el.className);
                  // Force show as fallback
                  el.style.setProperty('display', 'block', 'important');
                  el.style.setProperty('visibility', 'visible', 'important');
                  el.style.setProperty('opacity', '1', 'important');
                  setTimeout(() => { window[logKey] = false; }, 10000); // Reset after 10s
                }
              }
            }
          });
        }
      };

      // Run immediately and after delays to ensure DOM is ready
      // This handles cases where the DOM might not be fully updated yet
      // Reduced frequency to avoid spamming re-pushes
      ensureVisible();
      const timeout1 = setTimeout(ensureVisible, 100);
      const timeout2 = setTimeout(ensureVisible, 500);
      const timeout3 = setTimeout(ensureVisible, 1500); // Give script more time to load
      const raf = requestAnimationFrame(() => {
        requestAnimationFrame(ensureVisible);
      });

      return () => {
        clearTimeout(timeout1);
        clearTimeout(timeout2);
        clearTimeout(timeout3);
        cancelAnimationFrame(raf);
        if (elementObserver) {
          elementObserver.disconnect();
        }
        if (removalObserver) {
          removalObserver.disconnect();
        }
      };
    }, [location.pathname, location.search, pageId]); // Re-run on route changes
    
    // CRITICAL: Add a separate effect that ONLY ensures visibility on route changes
    // This runs AFTER the above effect to catch any elements that might have been removed
    useEffect(() => {
      // Small delay to let any removal/restoration settle
      const timeout = setTimeout(() => {
        const btn = document.getElementById('mainButton');
        const iframe = document.getElementById(IFRAME_ID);
        if (btn || iframe) {
          // Elements exist - ensure they're visible
          [btn, iframe].forEach((el) => {
            if (el) {
              el.style.setProperty('display', 'block', 'important');
              el.style.setProperty('visibility', 'visible', 'important');
              el.style.setProperty('opacity', '1', 'important');
            }
          });
        } else {
          // Elements don't exist - wait a bit longer before triggering restoration
          // The bot script might be in the process of creating them
          setTimeout(() => {
            const btn2 = document.getElementById('mainButton');
            const iframe2 = document.getElementById(IFRAME_ID);
            if (!btn2 && !iframe2) {
              // Still missing after delay - trigger restoration
              window.dispatchEvent(new CustomEvent('omni:routeChange', { 
                detail: { bot: 'omni8x8', pathname: location.pathname } 
              }));
            }
          }, 1000); // Wait 1 second before triggering restoration
        }
      }, 100);
      
      return () => clearTimeout(timeout);
    }, [location.pathname, location.search]);

    useEffect(() => {
      const ac = new AbortController();

      (async () => {
        // Check if iframe is ready
        const iframe = document.getElementById(IFRAME_ID);
        const iframeReady = iframe && iframe.contentWindow && isWidgetOpen();
        
        // If this is the first run, wait a bit for the iframe to be fully initialized
        if (firstRunRef.current) {
          await new Promise(r => requestAnimationFrame(() => requestAnimationFrame(r)));
          firstRunRef.current = false;
        }

        // On route changes (when firstRunRef is false), we need to force a context refresh
        // The bot middleware may not handle context updates properly, so we push a new config
        // with the updated pageId to force it to refresh, but only if the widget is open
        if (!firstRunRef.current && iframeReady) {
          // Wait a moment for document.title to update
          await new Promise(r => setTimeout(r, 100));
          
          // Push a new config with updated pageId to force context refresh
          // This is safe because the widget is already open and initialized
          const w = window;
          w._omnibotJs = w._omnibotJs || [];
          
          const collect = () => ({
            subjectLine: document.title || '',
            currentURL: window.location.href,
            browserDetails: getBrowserDetails(),
            screenResolution: getScreenResolution(),
            platform: getOS(),
            customerID,
            customerName,
            emailAddress,
          });
          
          const dataConfig = collect();
          const config = {
            deploymentId: '7d6cd007-48c0-4fe9-931c-f15a05ba0ad2',
            orgId: '9c6603a7-d5de-4f80-b5b0-6e13bfea2fe5',
            appId: '2ef241502396086a34add2327467b10b2',
            closeText: 'Virtual Assistant',
            infoText: "Hi, I'm the State of GTS Virtual Agent. I can answer questions, transfer to an agent and so much more.",
            language: 'en',
            userType: 'df_generic_rag_agent',
            pageId: location.pathname,
            environment: 'production',
            dataConfig,
          };
          
          console.log('[OMNI8x8] Pushing config to force context refresh on route change', { 
            pageId: config.pageId,
            pathname: location.pathname,
            subjectLine: config.dataConfig.subjectLine,
            currentURL: config.dataConfig.currentURL,
            fullConfig: config,
            configArrayLength: w._omnibotJs.length,
            previousConfig: w._omnibotJs.length > 0 ? w._omnibotJs[w._omnibotJs.length - 1] : null
          });
          
          // Push config to force refresh - the bot script should handle this gracefully
          // when the widget is already open
          w._omnibotJs.push(config);
          window.__omni8x8LastConfig = config;
          window.__omni8x8LastConfigPush = Date.now();
          
          console.log('[OMNI8x8] Config pushed, new array length:', w._omnibotJs.length, 'Last config pageId:', w._omnibotJs[w._omnibotJs.length - 1]?.pageId);
          
          // Check if bot script has a method to force context update
          // Some bot implementations have an updateContext or refreshContext method
          if (window._omnibotJs && typeof window._omnibotJs.updateContext === 'function') {
            console.log('[OMNI8x8] Found updateContext method, calling it');
            window._omnibotJs.updateContext(config.dataConfig);
          }
          
          // Also send context message via postMessage as backup
          const message = {
            type: 'context',
            pageId: location.pathname,
            subjectLine: document.title || '',
            currentURL: window.location.href,
            browserDetails: getBrowserDetails(),
            screenResolution: getScreenResolution(),
            platform: getOS(),
            customerID,
            customerName,
            emailAddress,
          };
          
          console.log('[OMNI8x8] Sending context message via postMessage', {
            message,
            iframeSrc: iframe?.src,
            iframeReady: iframeReady,
            iframeContentWindow: !!iframe?.contentWindow
          });
          
          // Set up a one-time message listener to see if bot responds
          const messageListener = (event) => {
            if (event.origin === 'https://app.demo.omnibots.ai' && 
                event.data?.channel === 'omnibot' && 
                (event.data?.event === 'context' || event.data?.event === 'context-updated' || event.data?.event === 'page-changed')) {
              console.log('[OMNI8x8] Bot responded to context update:', event.data);
              window.removeEventListener('message', messageListener);
            }
          };
          window.addEventListener('message', messageListener);
          
          // Remove listener after 5 seconds
          setTimeout(() => {
            window.removeEventListener('message', messageListener);
          }, 5000);
          
          await sendOmni('context', message, { 
            waitForEl: false,
            immediate: true,
            signal: ac.signal 
          });
          
          // Log what was actually sent
          console.log('[OMNI8x8] Context update complete', {
            configPushed: true,
            contextMessageSent: true,
            pageId: location.pathname,
            timestamp: Date.now(),
            configArrayLength: w._omnibotJs.length
          });
          
          // Check if there's a way to force the bot to re-read config
          // Some bot scripts expose methods on the window object
          if (window.omnibot && typeof window.omnibot.updateContext === 'function') {
            console.log('[OMNI8x8] Found window.omnibot.updateContext, calling it');
            window.omnibot.updateContext(config.dataConfig);
          }
        } else if (iframeReady) {
          // First run - just send context normally
          const message = {
            type: 'context',
            pageId: location.pathname,
            subjectLine: document.title || '',
            currentURL: window.location.href,
            browserDetails: getBrowserDetails(),
            screenResolution: getScreenResolution(),
            platform: getOS(),
            customerID,
            customerName,
            emailAddress,
          };
          
          await sendOmni('context', message, { 
            waitForEl: false,
            immediate: true,
            signal: ac.signal 
          });
        } else {
          // Iframe not ready yet - wait for it
          const message = {
            type: 'context',
            pageId: location.pathname,
            subjectLine: document.title || '',
            currentURL: window.location.href,
            browserDetails: getBrowserDetails(),
            screenResolution: getScreenResolution(),
            platform: getOS(),
            customerID,
            customerName,
            emailAddress,
          };
          
          await sendOmni('context', message, { 
            waitForEl: true,
            immediate: false,
            signal: ac.signal 
          });
        }
      })().catch((e) => {
        if (e?.name !== 'AbortError') console.error('[OMNI8x8] send failed', e);
      });

      return () => ac.abort();
    }, [location.pathname, location.search, customerID, customerName, emailAddress]);

    return null;
  }

  return <OmnibotBootstrap pageId={location.pathname} />;
}

// Custom comparison function to prevent remounts on route changes
// Only remount if userData or payload actually change (not just reference)
Omni8x8Bot.displayName = 'Omni8x8Bot';

export default React.memo(Omni8x8Bot, (prevProps, nextProps) => {
  // Only remount if userData or payload actually changed
  // This prevents remounts on route changes when props are the same
  const prevUserData = prevProps.userData;
  const nextUserData = nextProps.userData;
  const prevPayload = prevProps.payload;
  const nextPayload = nextProps.payload;
  
  // Compare userData fields that matter
  const userDataChanged = 
    (prevUserData?.customerID !== nextUserData?.customerID) ||
    (prevUserData?.fullName !== nextUserData?.fullName) ||
    (prevUserData?.emailAddress !== nextUserData?.emailAddress);
  
  // Compare payload IDs that matter
  const payloadChanged = 
    (prevPayload?.['custom:User_ID'] !== nextPayload?.['custom:User_ID']) ||
    (prevPayload?.email !== nextPayload?.email);
  
  // Only remount if userData or payload actually changed
  const shouldRemount = userDataChanged || payloadChanged;
  
  if (shouldRemount) {
    console.log('[OMNI8x8] Props changed, will remount', { userDataChanged, payloadChanged });
  }
  
  // Return true if props are equal (don't remount), false if different (remount)
  return !shouldRemount;
});

